import { CoreEnv } from '@eventbrite/context-gen';
import {
    COUNTRY_DEFAULT_LOCALE_MAP,
    getCurrencySymbol,
    getDefaultLocale,
} from '@eventbrite/intl';
import * as Sentry from '@sentry/browser';
import React, { useCallback, useEffect, useState } from 'react';
import { getCurrencySettings, getVenue } from '../api';
import {
    CurrencySettingsDTO,
    TimezoneDTO,
    TimezoneValues,
    VenueDTO,
    VenueType,
    VenueValues,
} from '../types';

interface LocalizationProps {
    countryCode: CoreEnv['localeInfo']['country_code'];
    currency: CoreEnv['localeInfo']['default_currency'];
    currencySymbol: string;
    locale: CoreEnv['localeInfo']['locale'];
    timezone: TimezoneDTO;
    logError?: (message: string, error: any) => void;
    updatePlace: (place: VenueValues, timezone: TimezoneValues) => void;
}

interface LocalizationProviderProps {
    initialValue?: Partial<LocalizationProps>;
    children: React.ReactNode;
    shouldFetchCurrency?: boolean;
}

const DEFAULT_COUNTRY_CODE = 'US';
const DEFAULT_CURRENCY = 'USD';
const DEFAULT_TIMEZONE = {
    timezone: 'America/Los_Angeles',
    label: 'Pacific Time (US & Canada)',
};
const DEFAULT_LOCALE = 'en_US';

const EVENT_SERVICE_SUPPORTED_LOCALES = [
    'en',
    'fr_FR',
    'pt_PT',
    'en_IE',
    'it_IT',
    'it',
    'nl_BE',
    'en_NZ',
    'nl_NL',
    'es_PE',
    'en_US',
    'en_SG',
    'en_HK',
    'es_CL',
    'fr_BE',
    'es',
    'de_CH',
    'nl',
    'pt',
    'es_CO',
    'en_AU',
    'de_AT',
    'es_AR',
    'es_ES',
    'en_GB',
    'en_CA',
    'fr',
    'pt_BR',
    'de',
    'fr_CH',
    'de_DE',
    'es_MX',
    'en_IN',
    'fr_CA',
    'sv',
    'en_FI',
    'en_DK',
];

const LocalizationContext = React.createContext<LocalizationProps | undefined>(
    undefined as LocalizationProps | undefined,
);

const LocalizationContextProvider: React.FC<LocalizationProviderProps> = ({
    children,
    initialValue,
    shouldFetchCurrency,
}) => {
    const logError = initialValue?.logError || Sentry.captureException;
    const [currency, setCurrency] = useState(
        initialValue?.currency || DEFAULT_CURRENCY,
    );

    const [countryCode, setCountryCode] = useState(
        initialValue?.countryCode || DEFAULT_COUNTRY_CODE,
    );

    const [locale, setLocale] = useState(
        initialValue?.locale || DEFAULT_LOCALE,
    );

    const [timezone, setUserTimezone] = useState<TimezoneDTO>(
        initialValue?.timezone || DEFAULT_TIMEZONE,
    );

    const [countryCurrencyMap, setCountryCurrencyMap] = useState<
        CurrencySettingsDTO['default_currencies_by_country']
    >({});

    const [settingsLoaded, setSettingsLoaded] = useState(false);

    const getCurrencyForCountryCode = useCallback(
        (countryCode: string): string => {
            return countryCurrencyMap[countryCode] || currency;
        },
        [countryCurrencyMap, currency],
    );

    const updateCurrencyForCountryCode = useCallback(
        (countryCode: string) => {
            const currency = getCurrencyForCountryCode(countryCode);
            setCurrency(currency);
        },
        [getCurrencyForCountryCode],
    );

    const updateCountryCode = useCallback(
        (countryCode: string) => {
            setCountryCode(countryCode);
            let countryLocale = getDefaultLocale(
                countryCode,
                COUNTRY_DEFAULT_LOCALE_MAP['US'],
            );
            if (!EVENT_SERVICE_SUPPORTED_LOCALES.includes(countryLocale)) {
                countryLocale = DEFAULT_LOCALE;
            }
            setLocale(countryLocale);
            updateCurrencyForCountryCode(countryCode);
        },
        [updateCurrencyForCountryCode],
    );

    const updatePlace = useCallback(
        (venue: VenueValues, venueTimezone: TimezoneValues) => {
            if (venue.type === VenueType.Venue) {
                const venueId = venue?.id;
                getVenue(venueId)
                    .then((venue: VenueDTO) => {
                        const venueCountryCode =
                            venue?.address?.country || countryCode;
                        updateCountryCode(venueCountryCode);
                    })
                    .catch((error) => {
                        logError(
                            `Error in GET /venues/${venueId || 'unkwonwn'}`,
                            error as any,
                        );
                    });
            }
            if (venue.type === VenueType.Google) {
                const placeCountryCode = venue?.address?.country || countryCode;
                updateCountryCode(placeCountryCode);
            }

            setUserTimezone(venueTimezone || timezone);
        },
        [countryCode, logError, timezone, updateCountryCode],
    );

    const getSymbol = (currencyCode: string): string => {
        return getCurrencySymbol(currencyCode);
    };

    const currencySymbol = getSymbol(currency);

    useEffect(() => {
        let shouldUpdate = true;
        if (countryCode && settingsLoaded) {
            updateCurrencyForCountryCode(countryCode);
        }

        if (!settingsLoaded && shouldFetchCurrency) {
            getCurrencySettings()
                .then((currencySettings) => {
                    if (shouldUpdate) {
                        setCountryCurrencyMap(
                            currencySettings.default_currencies_by_country,
                        );
                    }
                })
                .catch((error) => {
                    logError(
                        'Error in GET checkout_settings/countries_currencies',
                        error as any,
                    );
                })
                .finally(() => {
                    setSettingsLoaded(true);
                });
        }
        return () => {
            shouldUpdate = false;
        };
    }, [
        countryCode,
        logError,
        settingsLoaded,
        shouldFetchCurrency,
        updateCurrencyForCountryCode,
    ]);

    return (
        <LocalizationContext.Provider
            value={{
                countryCode,
                currency,
                currencySymbol,
                locale,
                timezone,
                updatePlace,
            }}
        >
            {children}
        </LocalizationContext.Provider>
    );
};

const useLocalizationContext = () => {
    const context = React.useContext(LocalizationContext);
    if (context === undefined) {
        throw new Error(
            'useLocalizationContext must be used within a LocalizationContextProvider',
        );
    }
    return context;
};

export { LocalizationContextProvider, useLocalizationContext };
